/**
 * useMembraneReferenceCollections - Composable for managing reference collection fields
 *
 * Handles the complex logic of reference collection fields including:
 * - Loading data from Membrane with pagination
 * - Server-side search with debouncing and race condition protection
 * - Dropdown state management for searchable fields
 * - Keyboard navigation (up/down/enter)
 * - Detection of search capabilities
 */

import { DataRecord, MembraneClient } from "@membranehq/sdk";
import { computed, ref, Ref, toRaw } from "vue";

import { SelectOption } from "@/components/base/BaseInputSelect.vue";
import { ReferenceCollectionConfig } from "@/composables/useMembraneDynamicSchemaForm";
import { normalizeVarReference } from "@/utils/membrane";

export interface ReferenceCollectionField {
  key: string;
  referenceCollection: ReferenceCollectionConfig;
}

/**
 * Type guard for the `{ "$var": "field" }` reference format used in reference
 * collection parameters. Only checks for the presence of the `$var` key, which
 * matches the historical runtime behavior.
 */
export function isVarReference(value: object): value is { $var: string } {
  return "$var" in value;
}

export function useMembraneReferenceCollections(
  membrane: Ref<MembraneClient>,
  selectedConnectionId: Ref<string>,
  dynamicParameters: Ref<Record<string, unknown>>
) {
  // Reference Collection State Management
  /** Stores loaded data for non-searchable reference collections (full dataset) */
  const referenceCollectionData = ref<
    Record<string, Array<{ id: string; name: string }>>
  >({});

  /** Stores search results for searchable reference collections */
  const referenceCollectionSearchResults = ref<
    Record<string, Array<{ id: string; name: string }>>
  >({});

  /** Tracks whether each reference collection supports server-side search */
  const referenceCollectionSearchable = ref<Record<string, boolean>>({});

  /** Last completed search query for each field (used to detect when search changes) */
  const referenceCollectionSearchQuery = ref<Record<string, string>>({});

  /** Current user input for each field (real-time, before debounce) */
  const referenceCollectionCurrentQuery = ref<Record<string, string>>({});

  /** Loading state for each reference collection field */
  const referenceCollectionLoading = ref<Record<string, boolean>>({});

  // Custom Dropdown State (for searchable fields only)
  /** Tracks whether custom dropdown is open for each field */
  const referenceCollectionDropdownOpen = ref<Record<string, boolean>>({});

  /** Selected index in dropdown options for keyboard navigation */
  const referenceCollectionSelectedIndex = ref<Record<string, number>>({});

  /** Stores the display label for selected reference collection values */
  const referenceCollectionSelectedLabels = ref<Record<string, string>>({});

  // Search Debouncing and Race Condition Protection
  /** Active timeouts for debounced search requests */
  const searchTimeouts = ref<Record<string, ReturnType<typeof setTimeout>>>({});

  /** Request IDs to prevent race conditions in search results */
  const searchRequestIds = ref<Record<string, string>>({});

  /**
   * Reactive computed property that provides dropdown options for reference collection fields
   *
   * This is one of the most complex parts of the component. It determines which data to show
   * in each reference collection dropdown based on:
   * 1. Whether the field supports server-side search
   * 2. Whether the user has entered a search query (3+ chars)
   * 3. The current state of search results vs loaded data
   *
   * Key behaviors:
   * - Searchable fields with active search: Use search results (preserves relevance order)
   * - Non-searchable or short query: Use full dataset (alphabetically sorted)
   * - Reactive to all search state changes for real-time updates
   */
  const referenceCollectionOptions = computed(() => {
    const options: Record<string, SelectOption[]> = {};

    // We need to iterate over fields, but we don't have direct access to them here
    // So we iterate over all known fields in the data/searchResults
    const allFieldKeys = new Set([
      ...Object.keys(referenceCollectionData.value),
      ...Object.keys(referenceCollectionSearchResults.value),
    ]);

    for (const fieldKey of allFieldKeys) {
      const isSearchable = referenceCollectionSearchable.value[fieldKey];
      const currentQuery =
        referenceCollectionCurrentQuery.value[fieldKey] || "";
      const hasSearchQuery = currentQuery.length >= 3;

      // For searchable fields with active search, use search results
      if (isSearchable && hasSearchQuery) {
        const results = referenceCollectionSearchResults.value[fieldKey] || [];

        // Don't sort search results - keep Membrane's relevance order
        options[fieldKey] = results.map((item) => ({
          value: item.id,
          label: item.name,
          aliases: [],
        }));
      } else {
        // Otherwise use regular data (sorted alphabetically)
        const data = referenceCollectionData.value[fieldKey] || [];

        options[fieldKey] = data
          .map((item) => ({
            value: item.id,
            label: item.name,
            aliases: [],
          }))
          .sort((a: SelectOption, b: SelectOption) =>
            a.label.localeCompare(b.label)
          );
      }
    }

    return options;
  });

  /**
   * Core function for loading reference collection data from Membrane
   *
   * This function handles the complex logic of:
   * 1. Resolving parameter dependencies (e.g., $repositoryId references)
   * 2. Choosing between search vs pagination based on query and capabilities
   * 3. Handling Membrane pagination bugs with cursor workarounds
   * 4. Detecting search capabilities by examining data collection specs
   *
   * @param connectionId - Membrane connection ID
   * @param referenceCollection - Reference collection config from schema
   * @param fieldKey - Form field key for state management
   * @param searchQuery - Optional search query for server-side search
   * @returns Promise resolving to array of {id, name} objects for dropdown options
   */
  const loadReferenceCollectionData = async (
    connectionId: string,
    referenceCollection: ReferenceCollectionConfig,
    fieldKey: string,
    searchQuery?: string
  ): Promise<Array<{ id: string; name: string }>> => {
    try {
      referenceCollectionLoading.value[fieldKey] = true;

      // Build parameters for the reference collection
      const parameters = referenceCollection.parameters || {};

      // Resolve any parameter values from current dynamic parameters
      const resolvedParameters: Record<string, unknown> = {};
      for (const [paramKey, paramValue] of Object.entries(parameters)) {
        // Unwrap Vue Proxy to get the raw value
        const rawParamValue = toRaw(paramValue);

        if (
          typeof rawParamValue === "string" &&
          rawParamValue.startsWith("$")
        ) {
          // String format: "$workspace_id"
          const referencedParamKey = rawParamValue.slice(1);
          const referencedValue = dynamicParameters.value[referencedParamKey];
          if (referencedValue) {
            resolvedParameters[paramKey] = referencedValue;
          } else {
            // Dependent parameter not yet set - cannot load this reference collection
            return [];
          }
        } else if (
          typeof rawParamValue === "object" &&
          rawParamValue !== null &&
          isVarReference(rawParamValue)
        ) {
          // Object format: { "$var": "workspace_id" } or { "$var": "$.workspace_id" }
          const referencedParamKey = normalizeVarReference(rawParamValue.$var);
          const referencedValue = dynamicParameters.value[referencedParamKey];
          if (referencedValue) {
            resolvedParameters[paramKey] = referencedValue;
          } else {
            // Dependent parameter not yet set - cannot load this reference collection
            return [];
          }
        } else {
          // Static parameter value - use as-is
          resolvedParameters[paramKey] = rawParamValue;
        }
      }

      const dataCollection = membrane.value
        .connection(connectionId)
        .dataCollection(referenceCollection.key, resolvedParameters);

      // If we have a search query and this collection is searchable, use search
      if (searchQuery && referenceCollectionSearchable.value[fieldKey]) {
        const response = await dataCollection.search({ query: searchQuery });
        const records: DataRecord[] = response.records || [];
        return records.map((record) => ({
          id: record.id,
          name: record.name || record.id,
        }));
      }

      // Use pagination to load all records (for non-searchable collections)
      const allRecords: DataRecord[] = [];
      let cursor: string | undefined;
      let isFirstPage = true;

      do {
        const listParams = cursor ? { cursor } : {};
        const response = await dataCollection.list(listParams);
        const records: DataRecord[] = response.records || [];
        allRecords.push(...records);

        const nextCursor = response.cursor;

        // WORKAROUND: Membrane cursor bug
        // Sometimes Membrane returns the same cursor for consecutive pages,
        // causing infinite loops. When detected, we only use the first page.
        // See https://routine-company.slack.com/archives/C08EGE3TAQ2/p1758196477049709
        if (nextCursor && nextCursor === cursor) {
          // When this bug occurs, only use the first page to avoid duplicates
          // Remove the records from the second page since they're likely duplicates
          allRecords.splice(allRecords.length - records.length);
          break;
        }

        cursor = nextCursor;

        // OPTIMIZATION: Check for search capability on first page with pagination
        // If a collection has multiple pages but supports search, we stop loading
        // and let the user search instead of loading potentially thousands of records
        if (isFirstPage && cursor) {
          try {
            const spec = await dataCollection.get();
            const hasSearch = spec.search !== undefined;
            referenceCollectionSearchable.value[fieldKey] = hasSearch;

            if (hasSearch) {
              // Don't load all pages - return empty array so search UI is shown
              return [];
            }
          } catch {
            // If we can't get the spec, assume no search and continue loading all pages
            referenceCollectionSearchable.value[fieldKey] = false;
          }
        }

        isFirstPage = false;
      } while (cursor);

      return allRecords.map((record) => ({
        id: record.id,
        name: record.name || record.id,
      }));
    } catch (error) {
      window.console.error(
        `Failed to load reference collection data for ${fieldKey}:`,
        error
      );
      return [];
    } finally {
      referenceCollectionLoading.value[fieldKey] = false;
    }
  };

  /**
   * Loads reference collection data for all fields that have reference collections
   */
  const loadReferenceCollections = async (
    fields: ReferenceCollectionField[]
  ) => {
    if (!selectedConnectionId.value || !fields.length) return;

    // Load reference collections for all fields that have them
    for (const field of fields) {
      if (field.referenceCollection) {
        const data = await loadReferenceCollectionData(
          selectedConnectionId.value,
          field.referenceCollection,
          field.key
        );
        referenceCollectionData.value[field.key] = data;
      }
    }
  };

  /**
   * Handles user input in searchable reference collection fields
   *
   * Implements debounced search with the following logic:
   * 1. Updates current query immediately for reactive UI updates
   * 2. Clears results if query too short (< 3 chars)
   * 3. Debounces actual search requests by 300ms
   * 4. Clears previous search when query changes
   */
  const handleSearchInput = (
    fieldKey: string,
    value: string,
    onSearch: (fieldKey: string, searchQuery: string) => Promise<void>
  ) => {
    // Update current query immediately for reactive computed
    referenceCollectionCurrentQuery.value[fieldKey] = value;

    // If query is too short, clear search results
    if (value.length < 3) {
      referenceCollectionSearchResults.value[fieldKey] = [];
      referenceCollectionSearchQuery.value[fieldKey] = "";
      return;
    }

    // Check if this is a new search query
    const lastSearchQuery = referenceCollectionSearchQuery.value[fieldKey];
    if (lastSearchQuery !== value) {
      // Clear existing search results immediately when query changes
      referenceCollectionSearchResults.value[fieldKey] = [];

      // Clear existing timeout
      if (searchTimeouts.value[fieldKey]) {
        clearTimeout(searchTimeouts.value[fieldKey]);
      }

      // Set new timeout for debounced search
      searchTimeouts.value[fieldKey] = setTimeout(() => {
        onSearch(fieldKey, value);
      }, 300);
    }
  };

  /**
   * Executes the actual search request with race condition protection
   *
   * Race conditions can occur when:
   * 1. User types quickly, creating multiple overlapping search requests
   * 2. Network latency causes older requests to resolve after newer ones
   * 3. User changes input while previous search is still in progress
   *
   * Protection mechanism:
   * - Generates unique request ID for each search
   * - Only updates results if the request ID matches the latest one
   * - Also verifies the search query still matches user's current input
   */
  const onReferenceCollectionSearch = async (
    fieldKey: string,
    searchQuery: string,
    referenceCollection: ReferenceCollectionConfig
  ) => {
    if (!selectedConnectionId.value) return;

    // Only search if query has 3+ characters
    if (searchQuery.length < 3) {
      referenceCollectionSearchResults.value[fieldKey] = [];
      referenceCollectionSearchQuery.value[fieldKey] = "";
      return;
    }

    // Generate unique request ID to handle race conditions
    const requestId = `${fieldKey}-${Date.now()}-${Math.random()}`;
    searchRequestIds.value[fieldKey] = requestId;
    referenceCollectionSearchQuery.value[fieldKey] = searchQuery;

    const data = await loadReferenceCollectionData(
      selectedConnectionId.value,
      referenceCollection,
      fieldKey,
      searchQuery
    );

    // RACE CONDITION PROTECTION
    // Only update results if this is still the latest request AND
    // the user hasn't changed their input since this search started
    const currentQuery = referenceCollectionCurrentQuery.value[fieldKey];

    if (
      searchRequestIds.value[fieldKey] === requestId &&
      searchQuery === currentQuery
    ) {
      referenceCollectionSearchResults.value[fieldKey] = data;
      // Force reactivity by creating a new object reference
      referenceCollectionSearchResults.value = {
        ...referenceCollectionSearchResults.value,
      };
    }
    // If conditions don't match, silently discard stale results
  };

  const handleReferenceCollectionInput = (fieldKey: string, value: string) => {
    dynamicParameters.value[fieldKey] = value;
    referenceCollectionCurrentQuery.value[fieldKey] = value;

    // Clear the selected label when user starts typing
    delete referenceCollectionSelectedLabels.value[fieldKey];

    // Always show dropdown when user is typing
    referenceCollectionDropdownOpen.value[fieldKey] = true;
    referenceCollectionSelectedIndex.value[fieldKey] = 0;
  };

  const handleReferenceCollectionFocus = (fieldKey: string) => {
    // Always show dropdown on focus, even if no options initially
    referenceCollectionDropdownOpen.value[fieldKey] = true;
    referenceCollectionSelectedIndex.value[fieldKey] = 0;
  };

  const handleReferenceCollectionBlur = (fieldKey: string) => {
    // Use setTimeout to allow click events on dropdown items to fire first
    setTimeout(() => {
      referenceCollectionDropdownOpen.value[fieldKey] = false;
    }, 150);
  };

  const selectPreviousOption = (fieldKey: string) => {
    const currentIndex = referenceCollectionSelectedIndex.value[fieldKey] || 0;
    referenceCollectionSelectedIndex.value[fieldKey] = Math.max(
      currentIndex - 1,
      0
    );
  };

  const selectNextOption = (fieldKey: string, maxIndex: number) => {
    const currentIndex = referenceCollectionSelectedIndex.value[fieldKey] || 0;
    referenceCollectionSelectedIndex.value[fieldKey] = Math.min(
      currentIndex + 1,
      maxIndex
    );
  };

  const validateReferenceCollectionSelection = (
    fieldKey: string,
    onSelect: (fieldKey: string, option: SelectOption) => void
  ) => {
    const selectedIndex = referenceCollectionSelectedIndex.value[fieldKey] || 0;
    const option = referenceCollectionOptions.value[fieldKey]?.[selectedIndex];
    if (option) {
      onSelect(fieldKey, option);
    }
  };

  const selectReferenceCollectionOption = (
    fieldKey: string,
    option: SelectOption
  ) => {
    dynamicParameters.value[fieldKey] = option.value;
    referenceCollectionSelectedLabels.value[fieldKey] = option.label;
    referenceCollectionCurrentQuery.value[fieldKey] = "";
    referenceCollectionDropdownOpen.value[fieldKey] = false;
  };

  /**
   * Clears all reference collection state (for cleanup)
   */
  const clearReferenceCollections = () => {
    referenceCollectionData.value = {};
    referenceCollectionSearchResults.value = {};
    referenceCollectionLoading.value = {};
    referenceCollectionDropdownOpen.value = {};
    referenceCollectionSelectedIndex.value = {};
    referenceCollectionSelectedLabels.value = {};
    referenceCollectionSearchable.value = {};
    referenceCollectionSearchQuery.value = {};
    referenceCollectionCurrentQuery.value = {};

    // Clear any pending search timeouts
    Object.values(searchTimeouts.value).forEach(clearTimeout);
    searchTimeouts.value = {};
    searchRequestIds.value = {};
  };

  return {
    // State
    referenceCollectionData,
    referenceCollectionSearchResults,
    referenceCollectionSearchable,
    referenceCollectionSearchQuery,
    referenceCollectionCurrentQuery,
    referenceCollectionLoading,
    referenceCollectionDropdownOpen,
    referenceCollectionSelectedIndex,
    referenceCollectionSelectedLabels,
    referenceCollectionOptions,

    // Methods
    loadReferenceCollectionData,
    loadReferenceCollections,
    handleSearchInput,
    onReferenceCollectionSearch,
    handleReferenceCollectionInput,
    handleReferenceCollectionFocus,
    handleReferenceCollectionBlur,
    selectPreviousOption,
    selectNextOption,
    validateReferenceCollectionSelection,
    selectReferenceCollectionOption,
    clearReferenceCollections,
  };
}
